import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { isEqual } from 'date-fns';
import { Popover, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { TimeField } from '@mui/x-date-pickers/TimeField';
import HourSelectFieldOption from 'components/shared/field-adapters/hour-picker-field-adapter/HourPickerOption';
import StyledHourPickerGrid from 'common/theme/fields/hour-picker/StyledHourPickerGrid';
import StyledTextField from 'common/theme/fields/StyledTextfield';
import { convertToDate, resetTimeInDate } from 'common/utils/date';

function HourSelectFieldAdapter({
  dateFormat = 'hh:mm aaa',
  disablePast = false,
  disabled = false,
  input,
  options,
  renderInputProps,
  ...rest
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const id = open ? 'hour-select-popover' : undefined;
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const size = useMemo(() => (isDesktop ? 'medium' : 'small'), [isDesktop]);

  useEffect(() => {
    if (input.value && input.value instanceof Date) {
      const date = resetTimeInDate(input.value);
      input.onChange(date);
    }
  }, []);

  const handleClick = (event) => {
    // Solo abrir el popover si NO está deshabilitado
    if (!disabled) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleChange = (value) => {
    // date-fns v2 lanza si recibe strings: normalizamos ambos lados a Date antes de comparar
    // (input.value puede venir como string ISO al editar). Si alguno no es fecha válida, dejamos pasar.
    const current = convertToDate(input.value);
    const next = convertToDate(value);
    if (!current || !next || !isEqual(current, next)) {
      input.onChange(value);
    }
    setAnchorEl(null);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <div>
      <TimeField
        {...input}
        {...rest}
        disabled={disabled}
        format={dateFormat}
        value={convertToDate(input.value)}
        onClick={handleClick}
        onChange={handleChange}
        slots={{
          textField: StyledTextField
        }}
        slotProps={{
          textField: {
            ...renderInputProps,
            readOnly: true,
            size
          }
        }}
      />
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        PaperProps={{
          sx: {
            // El doble del ancho del campo para que quepan varias horas por fila, pero SIN pasarse
            // del viewport: "Desde" ocupa media pantalla en móvil, así que el doble se salía por
            // la derecha y la última columna de horas quedaba cortada e intocable.
            width: anchorEl ? Math.min(anchorEl.offsetWidth * 2 + 24, window.innerWidth - 32) : 300,
            maxWidth: 'calc(100vw - 32px)',
            boxSizing: 'border-box',
            maxHeight: 220,
            overflowY: 'auto'
          }
        }}
        // Sin esto el popup se pega al borde izquierdo del campo y se sale igual: con el margen,
        // floating-ui lo empuja hacia dentro cuando no cabe.
        marginThreshold={16}>
        <StyledHourPickerGrid>
          {options.map((hour) => (
            <HourSelectFieldOption
              disablePast={disablePast}
              hour={hour}
              key={hour.label}
              onClick={handleChange}
              selectedHour={input.value}
            />
          ))}
        </StyledHourPickerGrid>
      </Popover>
    </div>
  );
}

HourSelectFieldAdapter.propTypes = {
  dateFormat: PropTypes.string,
  disablePast: PropTypes.bool,
  disabled: PropTypes.bool,
  input: PropTypes.object.isRequired,
  options: PropTypes.array.isRequired,
  referenceDate: PropTypes.object.isRequired,
  renderInputProps: PropTypes.object.isRequired
};

export default HourSelectFieldAdapter;
